<template>
  <div>
    <div class="row">
      <div class="col s12">
        <custom-filter-bar v-model="selectedFilters" :theme="filterTheme" :filterFields="filtersFields" @filter="filterOffers()" />
      </div>
    </div>
    <div class="row margin-bot-lg">
      <div class="col s12">
        <job-board-offer-list
          v-if="paginatedOffers.length"
          :offers="paginatedOffers"
          :companies="companies"
          :show-company-logo="includeChildrenOffers && isParentCompany"
        />
        <table v-else>
          <tbody>
            <tr>
              <td colspan="1">{{ $t("job_portal.offers_empty") }}</td>
            </tr>
          </tbody>
        </table>
        <Pagination
          v-if="filteredOffers.length"
          :pagination="offersPagination"
          :message="$t('job_portal.title_pagination')"
          @changePage="changePage($event)" />
      </div>
    </div>
  </div>
</template>
<script>
import CustomFilterBar from "./../shared/CustomFilterBar.vue";
import JobBoardOfferList from "./JobBoardOfferList.vue";
import { getJobBoardOffers } from '../../api/job_board';
import Pagination from "./../utils/Pagination.vue"

export default {
  name: "JobBoardOffers",
  components: {
    CustomFilterBar,
    JobBoardOfferList,
    Pagination
  },
  props: {
    dataEndpoint: {
      type: String,
      required: true
    },
    isParentCompany: {
      type: Boolean,
      default: false
    },
    includeChildrenOffers: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      filterTheme: {
        common: {
          backgroundColor: "#fff",
          color: "#757575",
        }
      },
      filtersFields: [
        {
          field: 'location',
          label: this.$i18n.t(`job_portal.location`),
          icon: 'fas fa-map-marker-alt',
          type: "treeselect",
          visible: true,
          options: []
        },
        {
          field: 'area',
          label: this.$i18n.t(`job_portal.area`),
          icon: 'fas fa-briefcase',
          type: "treeselect",
          visible: true,
          options: []
        },
        {
          field: 'owner_company',
          icon: 'fas fa-building',
          label: this.$i18n.t(`job_portal.company`),
          type: "select",
          visible: this.isParentCompany ? true : false,
          options: []
        },
        {
          field: 'hire_mode',
          icon: 'fas fa-clock',
          label: this.$i18n.t(`job_portal.hire_mode`),
          type: "select",
          visible: true,
          options: []
        },
        {
          field: 'remote_work',
          icon: 'fas fa-globe-americas',
          label: this.$i18n.t(`job_portal.remote_work`),
          type: "select",
          visible: true,
          options: []
        },
        {
          field: 'search_text',
          icon: 'fas fa-search',
          label: this.$i18n.t(`job_portal.search`),
          type: "input",
          visible: true,
        },
      ],
      offers: [],
      companies: [],
      filteredOffers: [],
      selectedFilters: {},
      PER_PAGE_DEFAULT: 10,
      page: 1,
    };
  },
  computed: {
    offersPagination() {
      const totalOffers = this.filteredOffers.length;
      const lastPage = Math.ceil(totalOffers / this.PER_PAGE_DEFAULT);
      return {
        current_page: this.page,
        from: ((this.page - 1) * this.PER_PAGE_DEFAULT) + 1,
        last_page: lastPage,
        per_page: this.PER_PAGE_DEFAULT,
        to: (this.page * this.PER_PAGE_DEFAULT) > totalOffers ? totalOffers : (this.page * this.PER_PAGE_DEFAULT),
        total: totalOffers,
        next: this.page < lastPage ? this.page + 1 : null,
        prev: this.page > 1 ? this.page - 1 : null
      }
    },
    paginatedOffers() {
      const to = this.page > 1 ? ((this.page - 1) * this.PER_PAGE_DEFAULT) : 0;
      const from = to + this.PER_PAGE_DEFAULT;
      return this.filteredOffers.slice(to, from);
    }
  },
  async mounted() {
    try {
      let lang = null;
      if (this.dataEndpoint) {
        const result = await getJobBoardOffers(this.dataEndpoint);
        if (result.offers) {
          const vm = this;
          let langPromises = result.offer_langs.map(function(lang){
            return vm.$i18n.loadLocale(lang); 
          });
          await Promise.all(langPromises);
          
          this.offers = result.offers;
          this.filterOffers();
          this.filtersFields.forEach(field => {
            if (field.visible && field.field in result.filters) {
              const filterFound = result.filters[field.field];
              field.options = this.buildFilterOptions(filterFound, lang);
            }
          });
          this.companies = result.companies;
        }
      }
    } catch (error) {
        console.error("[JobBoardOffers] ERROR GETTING DATA: %o", error);
    }
  },
  methods: {
    buildFilterOptions(filter, lang) {
      let filterOptions = [];
      let optionsLangMap = filter.lang_map;
      let optionsLang = filter.lang??lang;
      for(let optionId in filter.options) {
        let option = filter.options[optionId];
        let name = option.name??'';
        if (optionsLangMap) {
          name = this.$i18n.transFromMap(optionsLangMap, optionId, optionsLang); 
        }
        let filterOption = {
          'id': optionId,
          'label': `${name} (${option.count})`,
        }
        if ('children' in option) {
          filterOption['children'] = this.buildFilterOptions(option.children, lang);
        }
        filterOptions.push(filterOption);
      }
      return filterOptions;
    },
    filterOffers() {
      this.filteredOffers = [...this.offers];
      for (const field in this.selectedFilters) {
        this.filteredOffers = this.filteredOffers.filter((offer) => {
          if (this.selectedFilters[field].length === 0) {
            return true;
          } else if (field === 'search_text') {
            const offerName = removeAccents(offer.name.toLowerCase()).trim();
            const searchText = removeAccents(this.selectedFilters[field].toLowerCase()).trim();
            return offerName.includes(searchText);
          } else if (field === 'location') {
            return this.selectedFilters[field].includes(offer.city) 
              || this.selectedFilters[field].includes(offer.country) 
              || this.selectedFilters[field].includes(offer.region) 
              || this.selectedFilters[field].includes(offer.district);
          } else if (field === 'area') {
            return this.selectedFilters[field].includes(offer.area) 
              || this.selectedFilters[field].includes(offer.subarea);
          } else if (field === 'owner_company') {
            return this.selectedFilters[field].includes(`${offer.owner_company}`);
          }
          return this.selectedFilters[field].includes(offer[`${field}`]);
        });
      };
      this.changePage(1);
    },
    changePage(page) {
      this.page = page;
    }
  },
};
</script>